import {AsyncResultRequestState} from "../types/asyncResult"
import {CheckImageSide} from "../resources/checkDeposits"
import {CheckImageVersion, getCheckPaymentImage} from "../resources/checkPayments"
import {useAsyncResult} from "./useAsyncResult"

export function isCheckImageAvailable(image: ArrayBuffer | null | undefined): boolean {
	return image != null && image.byteLength > 0
}

export function isCheckPaymentVersionAvailable(
	front: ArrayBuffer | null | undefined,
	back: ArrayBuffer | null | undefined
): boolean {
	return isCheckImageAvailable(front) && isCheckImageAvailable(back)
}

function matchImage(asyncResult: AsyncResultRequestState<ArrayBuffer | null, unknown>): ArrayBuffer | null | undefined {
	return asyncResult.match(
		() => undefined,
		(image) => image,
		() => null
	)
}

export function useCheckPaymentImages(accessToken: string, id: string) {
	const frontPrintedState = useAsyncResult(() =>
		getCheckPaymentImage({accessToken, id, side: CheckImageSide.Front, version: CheckImageVersion.Printed})
	)
	const frontDepositedState = useAsyncResult(() =>
		getCheckPaymentImage({accessToken, id, side: CheckImageSide.Front, version: CheckImageVersion.Deposited})
	)
	const backPrintedState = useAsyncResult(() =>
		getCheckPaymentImage({accessToken, id, side: CheckImageSide.Back, version: CheckImageVersion.Printed})
	)
	const backDepositedState = useAsyncResult(() =>
		getCheckPaymentImage({accessToken, id, side: CheckImageSide.Back, version: CheckImageVersion.Deposited})
	)

	const frontPrintedImage = matchImage(frontPrintedState)
	const frontDepositedImage = matchImage(frontDepositedState)
	const backPrintedImage = matchImage(backPrintedState)
	const backDepositedImage = matchImage(backDepositedState)

	const allSettled = [frontPrintedImage, frontDepositedImage, backPrintedImage, backDepositedImage].every(
		(image) => image !== undefined
	)

	const printedAvailable = isCheckPaymentVersionAvailable(frontPrintedImage, backPrintedImage)
	const depositedAvailable = isCheckPaymentVersionAvailable(frontDepositedImage, backDepositedImage)

	return {
		frontPrintedImage,
		frontDepositedImage,
		backPrintedImage,
		backDepositedImage,
		allSettled,
		printedAvailable,
		depositedAvailable,
	}
}
